import { useImages } from "@features/images/use-images";
import { useUser } from "@features/user/use-user";
import { Button, TextArea } from "@radix-ui/themes";
import { Lightbulb, ExternalLink } from "lucide-react";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ResponsiveModal } from "./responsive-modal";

const SUGGESTION_DISMISSED_KEY = "suggestion_modal_dismissed_at";
const MAX_SUGGESTION_LENGTH = 2000;

export const useShouldShowSuggestionModal = () => {
  const { user } = useUser();
  const { images } = useImages();

  // Check if already dismissed
  const dismissedAt = localStorage.getItem(SUGGESTION_DISMISSED_KEY);
  const wasDismissed = !!dismissedAt;

  // Check if account is at least 1 week old
  const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
  const accountCreatedAt = user.data?.created_at || Date.now();
  const isAccountOldEnough = Date.now() - accountCreatedAt >= oneWeekMs;

  // Check if user has more than 20 images
  const hasEnoughImages = (images.data?.length || 0) >= 20;

  // Should auto-show if:
  // - Never dismissed
  // - Account is at least 1 week old
  // - User has 20+ images
  const shouldShow =
    !wasDismissed &&
    isAccountOldEnough &&
    hasEnoughImages &&
    user.data &&
    images.data;

  return shouldShow;
};

interface SuggestionModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  forceOpen?: boolean;
}

export const SuggestionModal = ({
  isOpen: controlledIsOpen,
  onClose: controlledOnClose,
  forceOpen = false,
}: SuggestionModalProps = {}) => {
  const { t } = useTranslation();
  const { submitSuggestion } = useUser();
  const shouldAutoShow = useShouldShowSuggestionModal();

  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [suggestionText, setSuggestionText] = useState("");
  const [step, setStep] = useState<"input" | "thankyou">("input");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use controlled state if provided, otherwise internal state
  const isOpen =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = controlledOnClose
    ? (open: boolean) => {
        if (!open) controlledOnClose();
      }
    : setInternalIsOpen;

  useEffect(() => {
    if (shouldAutoShow && !forceOpen && controlledIsOpen === undefined) {
      // Small delay before showing
      const timer = setTimeout(() => {
        setInternalIsOpen(true);
        posthog.capture("suggestion_modal_shown", { trigger: "auto" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [shouldAutoShow, forceOpen, controlledIsOpen]);

  const handleClose = () => {
    localStorage.setItem(SUGGESTION_DISMISSED_KEY, Date.now().toString());
    setIsOpen(false);
    setStep("input");
    setSuggestionText("");
  };

  const handleSubmit = async () => {
    if (!suggestionText.trim()) return;

    setIsSubmitting(true);

    posthog.capture("suggestion_submitted", {
      length: suggestionText.length,
    });

    try {
      await submitSuggestion(suggestionText.slice(0, MAX_SUGGESTION_LENGTH));
      setStep("thankyou");
    } catch (error) {
      console.error("Failed to submit suggestion:", error);
    }

    setIsSubmitting(false);
  };

  const renderInputStep = () => (
    <div className="py-4">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
          <Lightbulb className="w-8 h-8 text-white" />
        </div>
      </div>
      <h3 className="text-xl font-semibold mb-2 text-slate-900 dark:text-white text-center">
        {t("suggestion_modal.title")}
      </h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 text-center">
        {t("suggestion_modal.subtitle")}
      </p>
      <TextArea
        value={suggestionText}
        onChange={(e) =>
          setSuggestionText(e.target.value.slice(0, MAX_SUGGESTION_LENGTH))
        }
        placeholder={t("suggestion_modal.placeholder")}
        className="w-full min-h-[120px] mb-2"
      />
      <p className="text-xs text-slate-400 mb-4 text-right">
        {suggestionText.length}/{MAX_SUGGESTION_LENGTH}
      </p>
      <Button
        size="3"
        className="w-full mb-3"
        onClick={handleSubmit}
        disabled={isSubmitting || suggestionText.trim().length === 0}
      >
        {isSubmitting
          ? t("suggestion_modal.submitting")
          : t("suggestion_modal.submit")}
      </Button>
      <p className="text-xs text-slate-500 dark:text-slate-500 text-center">
        <ExternalLink className="w-3 h-3 inline mr-1" />
        {t("suggestion_modal.public_hint")}
      </p>
    </div>
  );

  const renderThankYouStep = () => (
    <div className="text-center py-8">
      <div className="flex justify-center mb-4">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 flex items-center justify-center">
          <span className="text-4xl">💡</span>
        </div>
      </div>
      <h3 className="text-2xl font-bold mb-2 text-slate-900 dark:text-white">
        {t("suggestion_modal.thankyou_title")}
      </h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
        {t("suggestion_modal.thankyou_message")}
      </p>
      <Button size="3" variant="soft" className="w-full" onClick={handleClose}>
        {t("suggestion_modal.close")}
      </Button>
    </div>
  );

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      height="auto"
      maxHeight="80vh"
      className="!max-w-md"
    >
      <div className="px-2">
        {step === "input" && renderInputStep()}
        {step === "thankyou" && renderThankYouStep()}
      </div>
    </ResponsiveModal>
  );
};

// Hook to manage the suggestion modal state from outside
export const useSuggestionModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const openModal = () => {
    setIsOpen(true);
    posthog.capture("suggestion_modal_shown", { trigger: "manual" });
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return { isOpen, openModal, closeModal };
};
